// Shared solutions data. Imported by both the /solutions index page and the
// individual /solutions/:slug detail pages so the two views cannot drift out
// of sync. Slugs are the source of truth for the URL and for the
// `service_slug` param on the GTM `view_service` event.

export const solutions = [
  {
    id: 1,
    slug: "ai-powered-growth",
    number: "01",
    title: "AI-Powered Growth",
    tagline: "More output. More intelligence. Less overhead.",
    metaTitle: "AI-Powered Growth | Radius Consulting",
    metaDescription:
      "AI-driven marketing systems that let your business create faster, target smarter and measure with precision. Custom AI workflows executed by our in-house team.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/d61c48e0-6244-47f1-a4f0-8b3e33bd12cd/images/ffabb605ad1ad36c386970542629eb7c487d27a560424856b21e4c5b8bfead26.png",
    description:
      "We build AI-driven marketing systems that let your business create faster, target smarter, and measure with precision. This isn't off-the-shelf automation, it's custom workflow design built around your brand, your audience, and your growth goals, executed by our in-house team.",
    services: [
      {
        name: "AI Marketing Audit & Roadmap",
        description:
          "A structured diagnostic of where your business stands today and a clear, prioritised plan for where AI can drive the most growth. The right starting point before any implementation.",
      },
      {
        name: "AI Workflow Development",
        description:
          "Custom automation frameworks across content production, campaign management, reporting and customer communication, designed to scale your marketing without scaling your headcount.",
      },
      {
        name: "AI-Powered Content at Scale",
        description:
          "Brand-consistent content, copy, imagery, video and localised assets produced at volume using GenAI tools managed by our creative team. Speed without sacrificing standards. Can be implemented for your team in your business as well.",
      },
      {
        name: "AI-Enhanced Performance Marketing",
        description:
          "Smarter campaigns driven by real-time data, predictive targeting and AI-optimised spend. Integrated across paid media, email and conversion, with analytics that actually tell you what to do next.",
      },
      {
        name: "AI Service Integration & Toolkits",
        description:
          "We identify, configure and embed the right AI tools into your existing business, from CRM to content to customer service, and build the internal capability for your team to use them confidently.",
      },
      {
        name: "GEO & AEO - AI Visibility for LLMs",
        description:
          "We audit and optimise how your brand is represented across ChatGPT, Perplexity, Claude, Gemini, and Google AI Overviews. Using our tool we then build a structured programme to ensure you're consistently cited, accurately described, and recommended ahead of competitors in AI-generated answers.",
      },
    ],
    outcomes: [
      "Faster content, campaign and reporting cycles without adding headcount",
      "Sharper targeting and spend efficiency across paid, email and conversion",
      "Consistent brand presence across LLMs, AI Overviews and traditional search",
    ],
    idealFor:
      "Businesses that are already running marketing at pace and now want to add AI as an execution layer, not a novelty. Best suited to SMMEs turning over R10m+ who feel their team is stretched and want more output per person.",
  },
  {
    id: 2,
    slug: "brand-marketing-design",
    number: "02",
    title: "Brand, Marketing & Design",
    tagline: "Built to be recognised. Designed to perform.",
    metaTitle: "Brand, Marketing & Design | Radius Consulting",
    metaDescription:
      "Brand strategy, growth marketing, creative execution, digital product design and retail experience. Full-service brand building that grows with your business.",
    image:
      "https://customer-assets.emergentagent.com/job_marketing-hub-431/artifacts/ehkmfmlc_Screenshot%202026-04-14%20at%2016.37.32.png",
    description:
      "We develop brands that earn trust and run campaigns that convert. From foundational identity work to integrated campaign execution and in-store experience, this solution covers the full breadth of what it takes to build a brand that grows with your business, not just one that looks good at launch.",
    services: [
      {
        name: "Brand Strategy & Evolution",
        description:
          "Positioning, architecture, identity systems and messaging frameworks that connect your business vision to what the market actually wants. For brands starting out or ready for their next chapter.",
      },
      {
        name: "Growth Marketing",
        description:
          "Integrated campaigns built on insight across paid, earned and owned channels. We plan, execute and optimise, with performance at the centre and brand integrity intact throughout.",
      },
      {
        name: "Creative Execution",
        description:
          "Campaign creative, art direction, motion design, video production and print brought to life by our in-house creative team. Work that performs across every channel and holds up in every context.",
      },
      {
        name: "Digital Product Design",
        description:
          "UI/UX design for web and mobile that puts the customer's experience first. From prototyping through to design systems and responsive build, products that work as well as they look.",
      },
      {
        name: "Retail & Last-Mile Experience",
        description:
          "In-store design, journey mapping, POS communications and packaging, bridging your digital brand into the physical spaces where purchase decisions are made.",
      },
    ],
    outcomes: [
      "A brand that is recognisable, defensible and priced above the category average",
      "Campaigns that hold brand integrity while still hitting performance targets",
      "A consistent customer experience from ad, to site, to shelf",
    ],
    idealFor:
      "Founders and marketing leaders who know a strong brand is a growth lever, not decoration, and want a partner that will run the whole stack from strategy through to campaign, product and store execution.",
  },
  {
    id: 3,
    slug: "go-to-market-commerce",
    number: "03",
    title: "Go-To-Market & Commerce",
    tagline: "From idea to market. From market to revenue.",
    metaTitle: "Go-To-Market & Commerce | Radius Consulting",
    metaDescription:
      "Growth strategy, website and e-commerce build, and pitch support. The commercial infrastructure that turns a business into a revenue-generating operation.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/d61c48e0-6244-47f1-a4f0-8b3e33bd12cd/images/82856d3e7e2381257347313a3738347fc4732714469d94f047f8809e39936a5d.png",
    description:
      "We build the commercial infrastructure that turns a business into a market-ready, revenue-generating operation. Strategy, digital presence, e-commerce capability structured for the realities of the SMME environment and built to sustain growth beyond the launch moment.",
    services: [
      {
        name: "Growth Strategy & Market Planning",
        description:
          "Market entry, competitive positioning and channel strategy grounded in data and tailored to where your business is right now. A clear growth plan, not a generic playbook.",
      },
      {
        name: "Website & Go-To-Market Development",
        description:
          "End-to-end website development paired with a launch strategy that drives traffic and converts from day one. SEO, content architecture and analytics included.",
      },
      {
        name: "E-Commerce Build & Execution",
        description:
          "Full e-commerce solutions on Shopify, from platform build and product catalogue to payment integration, CRO and retention campaigns. Built to convert browsers into loyal customers.",
      },
      {
        name: "Pitch & Funding Support",
        description:
          "Investor-ready pitch decks, business narrative development and presentation coaching for SMMEs preparing for funding rounds.",
      },
    ],
    outcomes: [
      "A launch strategy and site that convert from day one, not months later",
      "An e-commerce stack that is built to retain and repeat, not just to acquire",
      "A pitch and narrative that raises capital without diluting the story",
    ],
    idealFor:
      "Businesses preparing to launch a new product, enter a new market, take e-commerce seriously for the first time, or raise a round of funding.",
  },
];

export const findSolutionBySlug = (slug) =>
  solutions.find((s) => s.slug === slug) || null;
